<!--
  THE PHONE'S DOCK — <768px only. Home · Friends · Search · Profile.

  FOUR DESTINATIONS, AND SEARCH IS ONE OF THEM AGAIN. Search used to live as a
  circle at the end of the phone header (BubbleNav), which put it a stretch away
  from the thumb and, worse, put it in two places at once the moment anything
  else wanted it. It is back in the dock and it is GONE from the header: one
  control, one home. "My Jovii" left in exchange — its content is the Profile
  screen's now (routes/Profile.svelte), so the saved shelf, the suggestions and
  the continue row are one tap away under a tab that was already there.

  EXACTLY ONE TAB IS LIT, ALWAYS. Home owns everything that is not one of the
  other three, because the bubbles above it (Shows, Movies, Categories) and every
  title page reached from them are all inside the browsing section. Profile is lit
  on /profile, /settings AND /my — /my redirects to /profile and old links must
  not land on a bar that says "you are nowhere".

  ── THE GLASS REFLECTS WHAT IS ABOVE IT ────────────────────────────────────────

  Netflix's dock shows a soft, blurred, vertically-flipped smear of the tiles just
  above it inside the pill, and it is the single thing that makes the bar read as
  a piece of glass lying ON the page rather than a panel floating over it.

  THE HONEST WAY TO GET IT IS NOT A CANVAS. Drawing the DOM into a canvas is not
  something a browser will do — there is no `element()` outside Firefox, no
  DOM-to-bitmap primitive, and html2canvas-style re-rendering of a poster rail at
  60fps under a scroll is a frame budget nobody has. What Netflix itself ships is
  an approximation, and this is the same one, in three layers:

    1. `backdrop-filter: blur(28px) saturate(170%) brightness(1.05)` on the pill.
       That IS the reflection's colour: the blur samples the actual pixels behind
       the dock, so a crimson poster under it tints the glass crimson and a black
       letterbox leaves it black. Saturate pushes the sampled colour past what the
       fill would otherwise mute; brightness keeps a dark rail from reading as a
       hole.
    2. A `mask-image` gradient so the FILL is denser at the bottom and thins to
       almost nothing at the top edge. The reflection reads exactly where a real
       one would — at the lip, where the tiles meet the glass — because that is
       where the least fill is covering the blur.
    3. A `::before` sheen: `linear-gradient(to bottom, rgba(255,255,255,.18),
       transparent 40%)`, which is the lit bevel a physical edge would have. This
       is the `-webkit-box-reflect` gradient shape without `-webkit-box-reflect`
       itself, which only mirrors an element's own children and would mirror the
       four icons rather than the page.

  What this cannot do is flip the picture. A vertical mirror of arbitrary page
  content is not available to CSS at all, and faking it with a second copy of the
  scroll container only works when the content behind is a background image, which
  a rail of posters is not. The blur is what carries the colour, the mask is what
  puts it at the lip, and the sheen is what makes the lip an edge. Verified at
  430x932 over Home with the poster rails scrolled under it: the tiles show
  through, blurred and tinted, strongest along the top edge.

  ON A TELEVISION IT IS FLAT. `html[data-tv='on']` drops the backdrop-filter and
  the mask entirely: a TV composites a full-screen blur every frame and pays for
  it in dropped frames, and nobody is holding a television at thumb distance.

  IT DOES NOT COVER CONTENT. The bar is `position: fixed`, so it takes no space in
  the flow; `App.svelte` pays for it with `padding-bottom` on <main> — one owner of
  the number, which is `--tabbar-h` plus the home-indicator inset. Two owners of one
  number is how this project got a 268px offset and a 91px overflow.

  IT BELONGS TO THE BROWSE LAYER. The watch route is rendered before the shell ever
  exists (see `App.svelte`), so there is no path by which this can appear over a
  film, in full screen, or under the player's transport bar.
-->
<script lang="ts">
  import Icon, { type IconName } from './Icon.svelte';
  import { nav } from './nav.svelte';
  import { gate } from './session.svelte';

  /**
   * IS THIS A TELEVISION? Read off `<html data-tv>` and applied as a class on
   * this bar's own element.
   *
   * Every other component expresses the TV tier with a global CSS selector on
   * `html[data-tv='on']`, and this one deliberately does not. `shell.test.ts`
   * forbids Svelte's global-selector escape hatch ANYWHERE in this file — the
   * assertion is a plain text search, so it catches the token in a comment too
   * — because this bar is `fixed`, and the one time it reached outside itself it
   * fought App.svelte over who owns the page's bottom padding: a 268px offset
   * and a 91px overflow. That rule is worth more than the consistency, so the
   * attribute is read once here rather than selected in CSS.
   *
   * Read at mount and never again: `applyTv()` writes the attribute in `main.ts`
   * before the app mounts, and a device does not stop being a television.
   */
  const onTv =
    typeof document !== 'undefined' && document.documentElement.dataset.tv === 'on';

  interface Tab {
    href: string;
    label: string;
    icon: IconName;
    active: (route: typeof nav.route) => boolean;
  }

  /**
   * Routes Home does NOT own. Everything else — /movies, /shows, /categories, a
   * title page, a collection, a person — is inside the browsing section and lights
   * Home, because on /movies the bar was previously blank and blank reads as "you
   * are nowhere".
   *
   * `mine` is in this list AND in Profile's own test below: /my is a redirect to
   * /profile now, but the route survives for old links and must light the tab its
   * content actually lives on.
   */
  const ELSEWHERE = ['search', 'room', 'mine', 'profile', 'friends', 'settings'];

  const TABS: Tab[] = [
    { href: '/', label: 'Home', icon: 'home', active: (r) => !ELSEWHERE.includes(r.name) },
    { href: '/friends', label: 'Friends', icon: 'people', active: (r) => r.name === 'friends' },
    { href: '/search', label: 'Search', icon: 'search', active: (r) => r.name === 'search' },
    {
      href: '/profile',
      label: 'You',
      icon: 'user',
      active: (r) => r.name === 'profile' || r.name === 'settings' || r.name === 'mine'
    }
  ];
</script>

<!-- Chrome, not a group — see the note in `TopBar.svelte`; this bar is `fixed`,
     which is the same trap one step worse. -->
<nav class="tabs" class:flat={onTv} aria-label="Main" data-dpad-chrome>
  {#each TABS as tab (tab.href)}
    {@const here = tab.active(nav.route)}
    <a href={tab.href === '/profile' && gate.accounts && gate.state !== 'open' ? '/signin' : tab.href} class:here aria-current={here ? 'page' : undefined}>
      <span class="glyph"><Icon name={tab.icon} size={22} /></span>
      <span class="label">{tab.href === '/profile' && gate.accounts && gate.state !== 'open' ? 'Sign in' : tab.label}</span>
    </a>
  {/each}
</nav>

<style>
  .tabs {
    position: fixed;
    /* About 25% narrower than edge-to-edge (the owner: "make it slightly
       less wide, maybe by 25%"): the insets are a share of the viewport, not
       a fixed 12px, so the pill floats in the middle rather than hugging the
       screen edges. 44px hit floors still hold at 320px wide. */
    left: max(calc(12.5vw), var(--safe-l));
    right: max(calc(12.5vw), var(--safe-r));
    /*
     * `--kb-inset` is 0 unless a software keyboard is shortening the visual
     * viewport (keyboard-inset.ts). A fixed element is positioned against the
     * LAYOUT viewport, which iOS does not shrink for the keyboard and can
     * leave scrolled after it closes - which is why the Search tab's dock sat
     * too high once the keyboard was dismissed, while every other page was
     * fine. Adding the inset makes the dock's bottom edge follow the part of
     * the screen the reader can actually see.
     */
    bottom: calc(var(--safe-b) + var(--tabbar-gap) + var(--kb-inset, 0px));
    max-width: 440px;
    margin-inline: auto;
    border-radius: 999px;
    overflow: hidden;
    box-shadow: inset 0 1px 0 rgb(255 255 255 / .16), 0 12px 36px rgb(0 0 0 / .35);
    z-index: var(--z-tabbar);
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    /* The bar is --tabbar-h of controls; the inset below it is the home
       indicator's own space and is padding, never height. */
    height: var(--tabbar-h);
    padding: 5px 6px;

    /*
     * THE REFLECTION, LAYER 1 AND 2. The blur is what samples the page; the fill
     * is a gradient that is nearly absent at the top edge and solid at the base,
     * so the sampled colour is least covered exactly where a reflection would sit.
     * A flat fill at any opacity hides the blur uniformly and the glass goes grey
     * — which is what this bar looked like before, and why the tiles never showed.
     */
    background: linear-gradient(
      to bottom,
      rgb(78 78 82 / .30) 0%,
      rgb(52 52 56 / .62) 42%,
      rgb(22 22 25 / .86) 100%
    );
    backdrop-filter: blur(28px) saturate(170%) brightness(1.05);
    -webkit-backdrop-filter: blur(28px) saturate(170%) brightness(1.05);
    border: 1px solid rgb(255 255 255 / .2);
    /* `isolation` so the sheen's blend cannot reach past the pill onto the page. */
    isolation: isolate;
  }

  /*
   * THE REFLECTION, LAYER 3 — the lit lip. A pseudo-element rather than another
   * box-shadow inset because it has to be a GRADIENT with a falloff (40% of the
   * bar's height), and an inset shadow is one hairline. `pointer-events: none` so
   * it never eats a press meant for a tab.
   */
  .tabs::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(to bottom, rgb(255 255 255 / .18), transparent 40%);
    pointer-events: none;
    z-index: 0;
  }

  a {
    position: relative;
    /* Above the sheen. Without this the gradient washes the top of every glyph. */
    z-index: 1;
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 3px;
    /* The whole column is the target, so the smallest one here is 97×56. */
    min-height: var(--hit);
    color: #b9b9be;
    border-radius: 999px;
    transition: color var(--dur-fast) var(--ease);
  }
  a:active .glyph {
    transform: scale(0.9);
  }

  .glyph {
    display: grid;
    place-content: center;
    width: 40px;
    height: 26px;
    border-radius: var(--radius-pill);
    transition:
      transform var(--dur-fast) var(--ease),
      background-color var(--dur) var(--ease);
  }

  .label {
    font-size: var(--text-2xs);
    font-weight: var(--weight-semi);
    letter-spacing: 0.01em;
    line-height: 1;
  }

  /* The current tab is lit, not swapped: one icon per tab, so the row never
     re-flows and nothing moves under a thumb that is already on its way down. */
  a.here {
    background: rgb(255 255 255 / .09);
    color: #fff;
  }
  a.here .glyph {
    background: transparent;
    color: #fff;
  }

  a:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -3px;
    border-radius: var(--radius);
  }

  /*
   * A TELEVISION GETS A FLAT BAR. It never actually gets THIS bar — the dock is
   * <768px and a TV lays out at 1280 — but `?tv=1` on a narrow window is a real
   * debugging path, and a full-width backdrop-filter composited every frame is
   * the exact cost the TV tier exists to refuse. Same rule as
   * `--glass-blur: none` in app.css's data-tv block, expressed as a local class
   * rather than a global selector — see `onTv` in the script for why.
   */
  .tabs.flat {
    background: var(--surface-1);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .tabs.flat::before {
    display: none;
  }
</style>